import { useEffect, useState } from 'react'
import type { VersionState } from 'features/versions/types'
import { isRunningInElectron } from 'utils/electron'
import { appAPI } from 'utils/app-api'
import { InstallationRow } from './InstallationRow'

type InstallationEntry = {
  versionId: number
  state: VersionState
  startedAt: Date
}

/**
 * Downloads page shows all active and recent software installations
 * with real-time status updates from the Electron main process
 */
export function DownloadsPage() {
  const [installationEntries, setInstallationEntries] = useState<Record<number, InstallationEntry>>({})

  useEffect(() => {
    if (!isRunningInElectron) return

    // Fetch initial installation states
    const fetchInitialStates = async () => {
      try {
        const states = await appAPI.getAllVersionStates()
        if (states) {
          setInstallationEntries((prev) => {
            const updated = { ...prev }
            const now = new Date()

            Object.entries(states).forEach(([versionId, state]) => {
              const id = Number(versionId)
              updated[id] = {
                versionId: id,
                state,
                startedAt: updated[id]?.startedAt ?? now,
              }
            })

            return updated
          })
        }
      } catch (error) {
        console.error('Failed to fetch installation states:', error)
      }
    }

    fetchInitialStates()

    // Listen for real-time updates
    const removeListener = appAPI.onAllVersionStatesUpdate((states) => {
      setInstallationEntries((prev) => {
        const updated = { ...prev }
        const now = new Date()

        Object.entries(states).forEach(([versionId, state]) => {
          const id = Number(versionId)
          const prevEntry = prev[id]

          // Set start time if this is a new installation
          updated[id] = {
            versionId: id,
            state,
            startedAt: prevEntry?.startedAt ?? now,
          }
        })

        return updated
      })
    })

    return () => {
      removeListener?.()
    }
  }, [])

  // Sort installations by start time (newest first)
  const allInstallations = Object.values(installationEntries).sort(
    (a, b) => b.startedAt.getTime() - a.startedAt.getTime(),
  )

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Downloads</h1>
      </div>

      {allInstallations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <p className="text-gray-600">No active installations</p>
        </div>
      ) : (
        <div className="space-y-4">
          {allInstallations.map(({ versionId, state }) => (
            <InstallationRow key={versionId} versionId={versionId} versionState={state} />
          ))}
        </div>
      )}
    </div>
  )
}
